import { useState, useMemo } from "react";
import BaseQrTemplate from "./templates/BaseQrTemplate";
import { generateQrPdf } from "./PdfGenerator";
import { useEffect } from "react";
import trackEvent from "../../utils/analytics";


export default function QrDrawer({ onClose, hotelName, logo, slug, businessType, businessSubType }) {

    const [loading, setLoading] = useState(false);
    const [bgColor, setBgColor] = useState("#ffffff");
    const [suggestedColors, setSuggestedColors] = useState([]);
    const [textColor, setTextColor] = useState("#000000"); // black default

    const normalizedBusinessType = businessType?.toLowerCase();
    const isRestaurant = normalizedBusinessType === "restaurant";
    const isServiceBusiness = businessSubType?.toLowerCase() === "service";


    const unitLabel = isRestaurant ? "Table" : "Room";
    const unitParam = isRestaurant ? "table" : "room";

    const [startUnit, setStartUnit] = useState(1);
    const [unitCount, setUnitCount] = useState(5);
    const [previewUnit, setPreviewUnit] = useState(null);
    const [isOrderQr, setIsOrderQr] = useState(false);

    const [prevStartUnit, setPrevStartUnit] = useState(startUnit);
    const [prevUnitCount, setPrevUnitCount] = useState(unitCount);


    useEffect(() => {
        if (isOrderQr) {
            // 🔐 Save current values before overriding
            setPrevStartUnit(startUnit);
            setPrevUnitCount(unitCount);

            setStartUnit(0);
            setUnitCount(1);
            setPreviewUnit(0);
        } else {
            // 🔄 Restore previous values
            setStartUnit(prevStartUnit || 1);
            setUnitCount(prevUnitCount || 1);
            setPreviewUnit(null);
        }
    }, [isOrderQr]);

    useEffect(() => {
        if (isServiceBusiness) {
            setIsOrderQr(true);
        }
    }, [isServiceBusiness]);



    const extractDominantColor = (imgUrl) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = imgUrl;

        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = 40;
            canvas.height = 40;
            const ctx = canvas.getContext("2d");

            ctx.drawImage(img, 0, 0, 40, 40);
            const data = ctx.getImageData(0, 0, 40, 40).data;

            let r = 0, g = 0, b = 0, count = 0;
            for (let i = 0; i < data.length; i += 4) {
                r += data[i];
                g += data[i + 1];
                b += data[i + 2];
                count++;
            }

            r = Math.round(r / count);
            g = Math.round(g / count);
            b = Math.round(b / count);

            setSuggestedColors([
                `rgb(${r},${g},${b})`,
                "#ffffff",
                "#1f2937",
            ]);
        };
    };

    const presetColors = [
        "#ffffff",   // White (Free default)
        "#6cbb23",   // Zafflo green
        "#4175bd",   // Charcoal
        "#f5f5f4",   // Warm neutral
        "#fef3c7",   // Soft yellow
        "#fee2e2",   // Soft red
    ];


    const units = useMemo(
        () => Array.from({ length: unitCount }, (_, i) => startUnit + i),
        [startUnit, unitCount]
    );

    const activePreviewUnit = previewUnit ?? units[0];



    const qrUrl = (unit) => {
        if (unit === 0) {
            return `https://zafflo.com/menu/${slug}`;
        }
        return `https://zafflo.com/menu/${slug}?${unitParam}=${unit}`;
    };



    const SelectedTemplate = BaseQrTemplate;

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">

                {/* 🔹 Header */}
                <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-[#6cbb23]">
                        QR Code Generator
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-xl"
                    >
                        ×
                    </button>
                </div>

                {/* 🔹 Content */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">



                    {/* Order QR Toggle */}
                    <div className={`border rounded-xl p-4 transition-all
    ${isOrderQr ? "border-[#6cbb23] bg-[#6cbb23]/5" : "border-gray-200 bg-white"}
    ${isServiceBusiness ? "opacity-60 pointer-events-none" : ""}
`}
                    >

                        <label className="flex items-center justify-between cursor-pointer">
                            <div className="pr-4">
                                <p className="text-sm font-semibold text-gray-800">
                                    Delivery / In-Store QR
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Generate a single QR without table or room number
                                </p>
                            </div>

                            {/* Toggle Switch */}
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    checked={isOrderQr}
                                    disabled={isServiceBusiness}
                                    onChange={(e) => setIsOrderQr(e.target.checked)}
                                    className="sr-only"
                                />
                                <div
                                    className={`w-11 h-6 rounded-full transition-colors ${isOrderQr ? "bg-[#6cbb23]" : "bg-gray-300"
                                        }`}
                                />
                                <div
                                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isOrderQr ? "translate-x-5" : ""
                                        }`}
                                />
                            </div>
                        </label>
                    </div>




                    {/* Room Configuration */}
                    <div className={`bg-gray-50 border rounded-xl p-4 space-y-4 ${isOrderQr ? "opacity-50 pointer-events-none" : ""
                        }`}>
                        <p className="text-sm font-semibold text-gray-700">
                            {unitLabel} Details
                        </p>

                        <div className="flex gap-3">
                            {/* Starting Room */}
                            <div className="w-1/2 space-y-1">
                                <label className="text-xs text-gray-500">
                                    First {unitLabel.toLowerCase()} number
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    placeholder="e.g. 1"
                                    value={startUnit}
                                    disabled={isOrderQr}
                                    onChange={(e) => setStartUnit(Number(e.target.value))}

                                    className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-[#6cbb23]/30 outline-none"
                                />
                            </div>

                            {/* Total Rooms */}
                            <div className="w-1/2 space-y-1">
                                <label className="text-xs text-gray-500">
                                    Number of {unitLabel.toLowerCase()}s (max 60)
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    max={60}
                                    placeholder="e.g. 20"
                                    value={unitCount}
                                    disabled={isOrderQr}
                                    onChange={(e) =>
                                        setUnitCount(Math.min(60, Number(e.target.value)))
                                    }

                                    className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-[#6cbb23]/30 outline-none"
                                />
                            </div>
                        </div>

                        <p className="text-xs text-gray-500">
                            QR codes will be generated for {unitLabel.toLowerCase()}s{" "}
                            <strong>{units[0]}</strong> to{" "}
                            <strong>{units[units.length - 1]}</strong>
                        </p>
                    </div>


                    {/* Template Selector */}
                    <div className="mt-6">
                        <p className="text-sm font-medium mb-2">Background color</p>

                        <div className="flex gap-3 overflow-x-auto py-1">
                            {[...new Set([...suggestedColors, ...presetColors])].map((c) => {
                                const isActive = bgColor === c;

                                return (
                                    <button
                                        key={c}
                                        onClick={() => setBgColor(c)}
                                        className={`relative w-9 h-9 rounded-full transition-all
          ${isActive ? "scale-110 shadow-md" : "hover:scale-105"}
        `}
                                        style={{ backgroundColor: c }}
                                    >
                                        {isActive && (
                                            <span className="absolute inset-0 flex items-center justify-center">
                                                <span className="w-3 h-3 rounded-full bg-white shadow" />
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <p className="text-xs text-gray-400 mt-2">
                            QR code remains white for best scan quality
                        </p>
                    </div>


                    {/* Background Color */}
                    <div className="mt-6 space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                            Background Color
                        </label>

                        <div className="flex items-center gap-4">
                            {/* Color picker */}
                            <input
                                type="color"
                                value={bgColor}
                                onChange={(e) => setBgColor(e.target.value)}
                                className="w-12 h-12 p-0 border rounded cursor-pointer"
                                title="Choose background color"
                            />

                            {/* Hex value */}
                            <input
                                type="text"
                                value={bgColor.toUpperCase()}
                                onChange={(e) => setBgColor(e.target.value)}
                                className="border px-3 py-2 rounded text-sm w-32"
                            />

                            {/* Reset */}
                            <button
                                onClick={() => setBgColor("#ffffff")}
                                className="text-xs text-gray-500 hover:text-gray-700 underline"
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    {/* Text Color */}
                    <div className="mt-6 space-y-2">
                        <p className="text-sm font-medium text-gray-700">
                            Text Color
                        </p>

                        <div className="flex gap-4">
                            {[
                                { label: "Black", value: "#000000" },
                                { label: "White", value: "#ffffff" },
                            ].map((opt) => {
                                const isActive = textColor === opt.value;

                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => setTextColor(opt.value)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition
            ${isActive
                                                ? "bg-[#6cbb23] text-white border-[#6cbb23]"
                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"}
          `}
                                    >
                                        <span
                                            className="w-4 h-4 rounded-full border"
                                            style={{ backgroundColor: opt.value }}
                                        />
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>

                        <p className="text-xs text-gray-400">
                            Choose text color for hotel name & {unitLabel.toLowerCase()} number
                        </p>
                    </div>



                    {/* Unit Preview Selector */}
                    {!isOrderQr && (
                        <div className="mt-6">
                            <p className="text-sm font-semibold text-gray-700 mb-2">
                                Preview {unitLabel}
                            </p>

                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {units.slice(0, 10).map((unit) => (
                                    <button
                                        key={unit}
                                        onClick={() => setPreviewUnit(unit)}
                                        className={`px-3 py-1.5 rounded-full text-xs border whitespace-nowrap transition ${activePreviewUnit === unit
                                            ? "bg-[#6cbb23] text-white border-[#6cbb23]"
                                            : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"
                                            }`}
                                    >
                                        {unitLabel} {unit}
                                    </button>
                                ))}

                                {units.length > 10 && (
                                    <span className="text-xs text-gray-400 self-center">
                                        +{units.length - 10} more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}



                    {/* Preview */}
                    <div>
                        <p className="text-sm font-semibold mb-2 text-gray-700">
                            Preview (Scaled)
                        </p>

                        <div className="bg-gray-100 p-4 rounded-xl flex justify-center">
                            <div className="bg-white shadow-lg rounded-lg p-3 scale-[0.85] origin-top">
                                <div
                                    className="border rounded-lg p-4 transition-colors"
                                    style={{ backgroundColor: bgColor }}
                                >
                                    <SelectedTemplate
                                        hotelName={hotelName}
                                        logo={logo}
                                        unitLabel={unitLabel}       // NEW
                                        unitNumber={activePreviewUnit}
                                        qrValue={qrUrl(activePreviewUnit)}
                                        bgColor={bgColor}
                                        textColor={textColor}
                                    />
                                </div>

                            </div>
                        </div>

                        <p className="text-xs text-gray-500 mt-2 text-center">
                            Actual print size: 4 × 6 inches
                        </p>
                    </div>
                </div>

                {/* 🔹 Sticky Footer */}
                <div className="sticky bottom-0 bg-white border-t px-6 py-4">
                    <div className="flex items-center justify-between mb-3 text-sm text-gray-600">
                        <span>{unitCount} QR codes</span>
                    </div>

                    <button
                        disabled={unitCount < 1 || loading}
                        onClick={async () => {
                            setLoading(true);
                            await generateQrPdf({
                                units,
                                unitLabel,
                                unitParam,
                                hotelName,
                                logo,
                                slug,
                                bgColor,
                                textColor,
                            });
                            setLoading(false);
                        }}
                        className="w-full bg-[#6cbb23] hover:bg-[#5aa81e] text-white py-3 rounded-xl font-semibold transition disabled:opacity-60"
                    >
                        {loading ? "Generating PDF..." : "Generate Printable PDF"}
                    </button>
                </div>
            </div>
        </>
    );
}
